import { useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { TextureLoader } from 'three/src/loaders/TextureLoader'

import {
  useGLTF,
  Hud,
  OrbitControls,
  OrthographicCamera,
  shaderMaterial,
} from '@react-three/drei'
import { useFrame, useLoader, extend } from '@react-three/fiber'

import vertexShader from '@/webgl/shaders/items/vertex.glsl?raw'
import fragmentShader from '@/webgl/shaders/items/fragment.glsl?raw'

const SceneShaderMaterial = shaderMaterial(
  {
    uTexture: null,
  },
  vertexShader,
  fragmentShader,
)

extend({ SceneShaderMaterial })

import { lerp } from '@/utils/math'
import { goNext } from '../../store/reducers/progressReducer'

export default function Inspector({}) {
  const dispatch = useDispatch()

  const { currentIndex } = useSelector((state) => state.progress)

  let nodes = null

  try {
    const result = useGLTF(`/models/items/${currentIndex}.glb`)
    nodes = result.nodes
  } catch (error) {
    console.error(`Error loading GLTF for currentIndex ${currentIndex}:`, error)
    nodes = null
  }

  const bakedTexture = useLoader(TextureLoader, '/textures/items/items.jpg')
  bakedTexture.flipY = false

  const material = useRef(
    new SceneShaderMaterial({
      uTexture: bakedTexture,
    }),
  )
  const isLeaving = useRef(false)
  const group = useRef()

  // TODO - useMemo here instead
  const exitPositionY = -8
  const targetPositionY = 0

  const targetRotationY = Math.PI * 2
  const LERP_FACTOR = 0.01

  useFrame((state, delta) => {
    if (nodes && group.current && group.current.position.y < 0) {
      group.current.position.y = lerp(
        group.current.position.y,
        targetPositionY,
        LERP_FACTOR * delta * 120,
      )
      group.current.rotation.y = lerp(
        group.current.rotation.y,
        targetRotationY,
        LERP_FACTOR * delta * 120,
      )
    }

    if (nodes && isLeaving.current) {
      group.current.position.y = lerp(
        group.current.position.y,
        exitPositionY,
        LERP_FACTOR * delta * 120,
      )
      group.current.rotation.y = lerp(
        group.current.rotation.y,
        group.current.rotation.y + Math.PI,
        LERP_FACTOR * delta * 120,
      )
    }
  })

  useEffect(() => {
    let timeout = null

    const closeSubtitlesHandler = () => {
      isLeaving.current = true

      timeout = setTimeout(() => {
        dispatch(goNext())
      }, 1500)
    }

    document.addEventListener('closeSubtitles', closeSubtitlesHandler)

    return () => {
      document.removeEventListener('closeSubtitles', closeSubtitlesHandler)
      clearTimeout(timeout)
    }
  }, [])

  return (
    <>
      <Hud renderPriority={1}>
        <OrbitControls
          enablePan={false}
          zoomSpeed={0.5}
          maxZoom={375}
          minZoom={300}
          autoRotate
        />
        <OrthographicCamera makeDefault position={[0, 0, -50]} zoom={300} />
        {/* TODO - Refactor this to prevent copy/paste */}
        {currentIndex === 0 && (
          <group dispose={null}>
            <group
              ref={group}
              position={[0, -3, 0]}
              rotation={[0, 0, -Math.PI / 2]}
            >
              <mesh
                geometry={nodes.murs008.geometry}
                material={material.current}
              />
            </group>
          </group>
        )}
        {currentIndex === 1 && (
          <group dispose={null}>
            <group
              ref={group}
              position={[0, -3, 0]}
              rotation={[0, 0, 0]}
              scale={1.5}
            >
              <mesh
                geometry={nodes.Horse.geometry}
                material={material.current}
              />
            </group>
          </group>
        )}
        {currentIndex === 2 && (
          <group dispose={null}>
            <group
              ref={group}
              position={[0, -3, 0]}
              rotation={[0, 0, 0]}
              scale={0.8}
            >
              <mesh geometry={nodes.TV.geometry} material={material.current} />
            </group>
          </group>
        )}
        {currentIndex === 3 && (
          <group dispose={null}>
            <group
              ref={group}
              position={[0, -3, 0]}
              rotation={[0, 0, -Math.PI / 2]}
            >
              <mesh
                geometry={nodes.murs006.geometry}
                material={material.current}
              />
            </group>
          </group>
        )}
        {currentIndex === 4 && (
          <group dispose={null}>
            <group ref={group} position={[0, -3, 0]} rotation={[0, 0, 0]}>
              <mesh
                geometry={nodes.Bananas.geometry}
                material={material.current}
                scale={2}
              />
            </group>
          </group>
        )}
      </Hud>
    </>
  )
}
